/**
 * Settings > Communication Gateways (boards #233 and #232).
 *
 * The screen both board items referred to as if it existed. It did not: the
 * API exposed `CommunicationChannelViewSet` but nothing in the frontend ever
 * POSTed to it, `communications.ts` had only `getChannels`, and the Settings
 * hub listed profile / domain / branding and nothing else. Production has zero
 * `CommunicationChannel` rows as a direct result, which is in turn why every
 * email, SMS and WhatsApp send on the platform fails at "no channel
 * configured".
 *
 * Two things this page is careful about:
 *
 * 1. Credentials go IN and never come back. The server returns `secrets_set`
 *    (names only) and `config_public` (the non-secret settings), so an edit
 *    form shows "Set" beside a secret rather than its value, and leaving a
 *    secret field blank keeps the stored one instead of blanking it.
 *
 * 2. A saved gateway is not a working gateway. `is_configured` (credentials
 *    filled in) and `delivery_implemented` (an adapter exists that can talk to
 *    the provider) are shown separately. Since #259 exactly one adapter exists
 *    -- SMTP -- so an SMTP gateway really can send and every other provider
 *    still fails with `DeliveryNotConfigured`. Showing one green "Active"
 *    badge would be the same lie the old Test button told.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import { Badge, Button, Card, EmptyState, Input, Modal, ModalFooter, Select } from '@/components/common';
import { PageHeader } from '@/components/layout';
import { useAuthStore } from '@/store';
import type { Gateway, GatewayListRow, ProviderOption } from '@/services/communications';
import {
  useCreateGateway,
  useDeleteGateway,
  useGateway,
  useGateways,
  useProviderCatalogue,
  useTestGateway,
  useUpdateGateway,
} from '@/pages/communications/useCommunicationGateways';

/** Config keys that are yes/no rather than free text. */
const BOOLEAN_CONFIG_KEYS = new Set(['use_tls', 'use_ssl']);

function humanise(key: string): string {
  return key
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

interface EditorState {
  name: string;
  channel_type: string;
  provider: string;
  sender_id: string;
  sender_name: string;
  is_active: boolean;
  config: Record<string, string>;
}

const BLANK: EditorState = {
  name: '',
  channel_type: '',
  provider: '',
  sender_id: '',
  sender_name: '',
  is_active: true,
  config: {},
};

export function CommunicationGatewaysPage() {
  const { data: gateways, isLoading } = useGateways();
  const { data: catalogue } = useProviderCatalogue();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [isEditorOpen, setEditorOpen] = useState(false);
  const [testingGateway, setTestingGateway] = useState<GatewayListRow | null>(null);

  const { data: editingGateway } = useGateway(editingId);

  return (
    <div>
      <PageHeader
        title="Communication Gateways"
        subtitle="Email, SMS and WhatsApp providers used to send messages"
        breadcrumbs={[{ label: 'Settings', href: '/settings' }, { label: 'Communication Gateways' }]}
        actions={
          <Button
            leftIcon={<PlusIcon className="w-5 h-5" />}
            onClick={() => {
              setEditingId(null);
              setEditorOpen(true);
            }}
          >
            Add Gateway
          </Button>
        }
      />

      {!gateways?.some((gateway) => gateway.delivery_implemented) && <DeliveryNotice />}

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, index) => (
            <Card key={index} className="animate-pulse">
              <div className="h-4 w-40 bg-gray-200 rounded mb-3" />
              <div className="h-3 w-64 bg-gray-100 rounded" />
            </Card>
          ))}
        </div>
      ) : !gateways || gateways.length === 0 ? (
        <Card>
          <EmptyState
            title="No gateways configured"
            description={
              'Nothing can be emailed, texted or sent over WhatsApp until a gateway exists. ' +
              'Add one for each channel you want to use.'
            }
            action={
              <Button
                leftIcon={<PlusIcon className="w-5 h-5" />}
                onClick={() => {
                  setEditingId(null);
                  setEditorOpen(true);
                }}
              >
                Add Gateway
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {gateways.map((gateway) => (
            <GatewayCard
              key={gateway.id}
              gateway={gateway}
              onEdit={() => {
                setEditingId(gateway.id);
                setEditorOpen(true);
              }}
              onTest={() => setTestingGateway(gateway)}
            />
          ))}
        </div>
      )}

      <GatewayEditor
        isOpen={isEditorOpen}
        catalogue={catalogue}
        gateway={editingId ? editingGateway : undefined}
        onClose={() => {
          setEditorOpen(false);
          setEditingId(null);
        }}
      />

      <GatewayTestModal gateway={testingGateway} onClose={() => setTestingGateway(null)} />
    </div>
  );
}

/** Stated once, at the top, rather than implied by a badge on every card.
 *
 *  Shown only while NONE of the configured gateways has an adapter behind it.
 *  Once an SMTP gateway is listed the warning would be wrong, and a standing
 *  warning nobody can clear is one nobody reads. */
function DeliveryNotice() {
  return (
    <Card className="mb-6 border-amber-200 bg-amber-50">
      <div className="flex gap-3">
        <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-medium">Saving a gateway does not switch delivery on.</p>
          <p className="mt-1">
            Only the <span className="font-medium">SMTP</span> email provider has a working
            integration. A gateway on any other provider stores its credentials but still fails with
            &ldquo;delivery is not configured&rdquo;. Use{' '}
            <span className="font-medium">Send test</span> on a gateway to see exactly where it stops.
          </p>
        </div>
      </div>
    </Card>
  );
}

function GatewayCard({
  gateway,
  onEdit,
  onTest,
}: {
  gateway: GatewayListRow;
  onEdit: () => void;
  onTest: () => void;
}) {
  const deleteMutation = useDeleteGateway();

  return (
    <Card>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-900">{gateway.name}</h3>
            <Badge variant="info" size="sm">
              {gateway.channel_type_display}
            </Badge>
            <Badge variant="default" size="sm">
              {gateway.provider_display}
            </Badge>
            {gateway.is_configured ? (
              <Badge variant="success" size="sm">
                Credentials set
              </Badge>
            ) : (
              <Badge variant="warning" size="sm">
                Not configured
              </Badge>
            )}
            {!gateway.is_active && (
              <Badge variant="default" size="sm">
                Disabled
              </Badge>
            )}
          </div>

          {gateway.sender_id && (
            <p className="mt-1 text-xs text-gray-500">
              Sends as {gateway.sender_name ? `${gateway.sender_name} <${gateway.sender_id}>` : gateway.sender_id}
            </p>
          )}

          {!gateway.is_configured && gateway.missing_config.length > 0 && (
            <p className="mt-2 text-xs text-amber-700">
              Missing: {gateway.missing_config.map(humanise).join(', ')}
            </p>
          )}

          {!gateway.delivery_implemented && (
            <p className="mt-2 text-xs text-gray-500">
              No {gateway.provider_display} integration is implemented, so messages through this
              gateway will fail rather than send.
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={onTest}>
            Send test
          </Button>
          <Button variant="outline" size="sm" onClick={onEdit}>
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            aria-label={`Delete ${gateway.name}`}
            isLoading={deleteMutation.isPending}
            onClick={() => {
              if (window.confirm(`Delete the gateway "${gateway.name}"?`)) {
                deleteMutation.mutate(gateway.id);
              }
            }}
          >
            <TrashIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}

function GatewayEditor({
  isOpen,
  gateway,
  catalogue,
  onClose,
}: {
  isOpen: boolean;
  gateway?: Gateway;
  catalogue?: { channel_type: string; channel_type_display: string; providers: ProviderOption[] }[];
  onClose: () => void;
}) {
  const [form, setForm] = useState<EditorState>(BLANK);
  const createMutation = useCreateGateway();
  const updateMutation = useUpdateGateway();

  const isEdit = !!gateway;

  useEffect(() => {
    if (!isOpen) return;
    if (gateway) {
      const publicConfig: Record<string, string> = {};
      Object.entries(gateway.config_public ?? {}).forEach(([key, value]) => {
        publicConfig[key] = value == null ? '' : String(value);
      });
      setForm({
        name: gateway.name,
        channel_type: gateway.channel_type,
        provider: gateway.provider,
        sender_id: gateway.sender_id ?? '',
        sender_name: gateway.sender_name ?? '',
        is_active: gateway.is_active,
        config: publicConfig,
      });
    } else {
      setForm(BLANK);
    }
  }, [isOpen, gateway]);

  const channelOptions = useMemo(
    () => (catalogue ?? []).map((row) => ({ value: row.channel_type, label: row.channel_type_display })),
    [catalogue]
  );

  const providersForChannel = useMemo(
    () => catalogue?.find((row) => row.channel_type === form.channel_type)?.providers ?? [],
    [catalogue, form.channel_type]
  );

  const selectedProvider = providersForChannel.find((p) => p.value === form.provider);

  const configFields = selectedProvider
    ? [...selectedProvider.required_config, ...selectedProvider.optional_config]
    : [];

  const secretKeys = new Set(selectedProvider?.secret_config ?? []);
  const storedSecrets = new Set(gateway?.secrets_set ?? []);

  const missingRequired = (selectedProvider?.required_config ?? []).filter((key) => {
    if (form.config[key]?.trim()) return false;
    // On edit, a stored secret counts as filled even though the input is blank.
    return !(isEdit && storedSecrets.has(key));
  });

  const canSave =
    form.name.trim().length > 0 && !!form.channel_type && !!form.provider && missingRequired.length === 0;

  const handleSave = () => {
    // Only send config entries the admin actually typed. An untouched secret
    // field must not be sent as "" -- the server merges, so an omitted key
    // keeps its stored value and an empty string would wipe it.
    const config: Record<string, string> = {};
    Object.entries(form.config).forEach(([key, value]) => {
      if (value.trim()) config[key] = value.trim();
    });

    const payload = {
      name: form.name.trim(),
      channel_type: form.channel_type,
      provider: form.provider,
      sender_id: form.sender_id.trim(),
      sender_name: form.sender_name.trim(),
      is_active: form.is_active,
      config,
    };

    if (isEdit && gateway) {
      updateMutation.mutate({ id: gateway.id, data: payload }, { onSuccess: onClose });
    } else {
      createMutation.mutate(payload, { onSuccess: onClose });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit Gateway' : 'Add Gateway'} size="lg">
      <div className="space-y-4">
        <Input
          label="Name"
          name="gateway-name"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          placeholder="Transactional Email"
          required
        />

        <Select
          label="Channel"
          name="gateway-channel-type"
          placeholder="Select a channel"
          options={channelOptions}
          value={form.channel_type}
          disabled={isEdit}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, channel_type: e.target.value, provider: '', config: {} }))
          }
          required
        />

        {form.channel_type && (
          <Select
            label="Provider"
            name="gateway-provider"
            placeholder="Select a provider"
            options={providersForChannel.map((p) => ({ value: p.value, label: p.label }))}
            value={form.provider}
            onChange={(e) => setForm((prev) => ({ ...prev, provider: e.target.value, config: {} }))}
            required
          />
        )}

        {selectedProvider && (
          <div className="rounded-lg border border-gray-200 p-4 space-y-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {selectedProvider.label} credentials
            </p>

            {configFields.map((key) => {
              const isSecret = secretKeys.has(key);
              const alreadyStored = isSecret && storedSecrets.has(key);
              const isRequired = selectedProvider.required_config.includes(key);
              if (BOOLEAN_CONFIG_KEYS.has(key)) {
                // Stored as the strings "true"/"false": `config` is a bare
                // JSONField and the server reads it with `gateways.bool_config`,
                // which understands both. A free-text box here is how an admin
                // ends up with use_tls="ture" and an unexplained failure.
                const checked = form.config[key] === 'true';
                return (
                  <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      name={`gateway-config-${key}`}
                      aria-label={humanise(key)}
                      checked={checked}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          config: { ...prev.config, [key]: e.target.checked ? 'true' : 'false' },
                        }))
                      }
                      className="rounded border-gray-300"
                    />
                    {humanise(key)}
                  </label>
                );
              }
              return (
                <Input
                  key={key}
                  label={humanise(key)}
                  name={`gateway-config-${key}`}
                  type={isSecret ? 'password' : 'text'}
                  autoComplete="off"
                  value={form.config[key] ?? ''}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, config: { ...prev.config, [key]: e.target.value } }))
                  }
                  placeholder={alreadyStored ? 'Stored — leave blank to keep' : ''}
                  required={isRequired && !alreadyStored}
                  helperText={
                    alreadyStored
                      ? 'A value is stored. It is never sent back to this browser; leave blank to keep it.'
                      : isSecret
                        ? 'Stored write-only — it will not be shown again.'
                        : undefined
                  }
                />
              );
            })}

            {!selectedProvider.delivery_implemented && (
              <p className="text-xs text-amber-700">
                No {selectedProvider.label} integration is implemented yet. These credentials will be
                stored, but messages will still fail until one is written.
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Sender ID"
            name="gateway-sender-id"
            value={form.sender_id}
            onChange={(e) => setForm((prev) => ({ ...prev, sender_id: e.target.value }))}
            placeholder="no-reply@example.com"
          />
          <Input
            label="Sender name"
            name="gateway-sender-name"
            value={form.sender_name}
            onChange={(e) => setForm((prev) => ({ ...prev, sender_name: e.target.value }))}
            placeholder="Acme Insurance"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
            className="rounded border-gray-300"
          />
          Enabled
        </label>

        {isEdit && gateway && (
          <div className="rounded-lg bg-gray-50 p-4 text-xs text-gray-600 space-y-1">
            <div className="flex items-start gap-2">
              <InformationCircleIcon className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
              <div className="min-w-0">
                <p className="font-medium text-gray-700">Delivery receipt webhook</p>
                <p className="mt-1 break-all font-mono">{gateway.webhook_url}</p>
                <p className="mt-1">
                  {gateway.webhook_secret_set
                    ? 'A signing secret is set — signed payloads from the provider will be accepted.'
                    : 'No signing secret is set, so every incoming payload is rejected. Set the ' +
                      'webhook secret (or auth token) above before registering this URL.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {missingRequired.length > 0 && (
          <p className="text-xs text-amber-700">
            Still needed: {missingRequired.map(humanise).join(', ')}
          </p>
        )}
      </div>

      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!canSave}
          isLoading={createMutation.isPending || updateMutation.isPending}
        >
          {isEdit ? 'Save changes' : 'Create gateway'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function GatewayTestModal({ gateway, onClose }: { gateway: GatewayListRow | null; onClose: () => void }) {
  // #259: "send me a test" is what this button means, so it starts addressed
  // to whoever is looking at it. Still editable -- testing an SMS gateway
  // needs a phone number, not the admin's email.
  const callerEmail = useAuthStore((state) => state.user?.email) ?? '';
  const isPhone = gateway?.channel_type === 'sms' || gateway?.channel_type === 'whatsapp';
  const [recipient, setRecipient] = useState(isPhone ? '' : callerEmail);
  const testMutation = useTestGateway();
  const result = testMutation.data;

  useEffect(() => {
    if (gateway) {
      setRecipient(
        gateway.channel_type === 'sms' || gateway.channel_type === 'whatsapp' ? '' : callerEmail
      );
      testMutation.reset();
    }
    // `testMutation` is recreated each render; keying on the gateway is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateway, callerEmail]);

  if (!gateway) return null;

  const isPhoneChannel = gateway.channel_type === 'sms' || gateway.channel_type === 'whatsapp';

  return (
    <Modal isOpen onClose={onClose} title={`Test ${gateway.name}`}>
      <div className="space-y-4">
        <Input
          label={isPhoneChannel ? 'Send to (phone)' : 'Send to (email)'}
          name="gateway-test-recipient"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder={isPhoneChannel ? '9876543210' : 'you@example.com'}
          required
        />

        {result && (
          <div
            className={
              result.ok
                ? 'rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-900'
                : 'rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-900'
            }
            role="status"
          >
            {result.ok ? (
              <p>
                The gateway accepted the message (status: {result.status}).
                {result.message_id && <span className="block font-mono text-xs mt-1">{result.message_id}</span>}
              </p>
            ) : (
              <>
                <p className="font-medium">Nothing was sent.</p>
                <p className="mt-1">{result.error}</p>
                {result.missing_config && result.missing_config.length > 0 && (
                  <p className="mt-1">Missing: {result.missing_config.map(humanise).join(', ')}</p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
        <Button
          disabled={!recipient.trim()}
          isLoading={testMutation.isPending}
          onClick={() => testMutation.mutate({ id: gateway.id, recipient: recipient.trim() })}
        >
          Send test
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default CommunicationGatewaysPage;
